import { redirect } from "@tanstack/react-router";
import type { ParsedToken } from "firebase/auth";

import { auth } from "@/lib/firebase/client";
import { vaultAppUrl } from "@/lib/vault-app";

// Single source of truth for role-based home routing. Mirrors the redirect
// logic in src/routes/app/index.tsx so guards and the /app index agree.
export function resolveHomePath(claims: ParsedToken): string {
  if (claims.platform_admin) {
    return "/admin/firms";
  }
  const activeOrg = claims.active_org as string | undefined;
  if (activeOrg?.trim()) {
    return "/app/clients";
  }
  // Vault owners/heirs now live in the standalone vault app; the advisor app has
  // no in-app home for a no-org user, so fall back to sign-in. (Post auth-cutover
  // these users authenticate on the vault origin and never reach this app.)
  return "/signin";
}

// Shared beforeLoad guard to enforce authentication and preserve redirect
export async function beforeAuthGuard({
  location,
}: {
  location: { href: string; pathname: string };
}) {
  // Ensure Firebase auth state is initialized before checking currentUser
  if (typeof auth.authStateReady === "function") {
    await auth.authStateReady();
  }

  // If user is not authenticated after auth state is ready, choose destination and redirect
  if (!auth.currentUser) {
    const pathname = location.pathname;

    // Decide where to send unauthenticated users based on origin
    const shouldRegister =
      pathname.startsWith("/checkout") || pathname.startsWith("/join");

    throw redirect({
      to: shouldRegister ? "/register" : "/signin",
      search: {
        redirect: location.href,
      },
    });
  }
}

// Shared beforeLoad guard to redirect authenticated users away from guest-only routes
export async function beforeGuestGuard() {
  // Ensure Firebase auth state is initialized before checking currentUser
  if (typeof auth.authStateReady === "function") {
    await auth.authStateReady();
  }

  // If already authenticated, send the user to their role-appropriate home.
  if (auth.currentUser) {
    const token = await auth.currentUser.getIdTokenResult();
    throw redirect({ to: resolveHomePath(token.claims) });
  }
}

// Vault-owner only — redirect org members away from /app/vault/*
export async function beforeVaultClientGuard({
  location,
}: {
  location: { href: string; pathname: string };
}) {
  await beforeAuthGuard({ location });
  const token = await auth.currentUser!.getIdTokenResult();
  const activeOrg = token.claims.active_org as string | undefined;
  if (activeOrg?.trim()) {
    throw redirect({ to: "/app/clients" });
  }
}

// Org member only — redirect vault owners away from /organisation/*
export async function beforeOrgMemberGuard({
  location,
}: {
  location: { href: string; pathname: string };
}) {
  await beforeAuthGuard({ location });
  const token = await auth.currentUser!.getIdTokenResult();
  const activeOrg = token.claims.active_org as string | undefined;
  const isPlatformAdmin = !!token.claims.platform_admin;
  if (!activeOrg?.trim() && !isPlatformAdmin) {
    // Vault owners belong to the standalone vault app (separate origin).
    throw redirect({ href: vaultAppUrl("/") });
  }
}

// Guard to enforce platform_admin custom claim
export async function beforePlatformAdminGuard({
  location,
}: {
  location: { href: string; pathname: string };
}) {
  // Enforce basic authentication first
  await beforeAuthGuard({ location });

  if (!auth.currentUser) {
    throw redirect({
      to: "/signin",
      search: {
        redirect: location.href,
      },
    });
  }

  const token = await auth.currentUser.getIdTokenResult();
  const isPlatformAdmin = !!token.claims.platform_admin;

  if (!isPlatformAdmin) {
    // Send non-admins to their role-appropriate home rather than always
    // /app/clients (which a vault owner cannot access and would bounce off).
    throw redirect({ to: resolveHomePath(token.claims) });
  }
}
